import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom'; 
import config from '../../constants/config.js';
import './../../styles/Dashboard.css';

function Search() {
    const [ searchTerm, setSearchTerm ] = useState( "" );
    const [ debouncedSearchTerm, setDebouncedSearchTerm ] = useState( "" );
    const [ results, setResults ] = useState( [] );
    const [ page, setPage ] = useState( 1 );
    const [ hasMore, setHasMore ] = useState( true );
    const [ loading, setLoading ] = useState( false );
    const debounceTime = 500;

    const popupRef = useRef();

     useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
            setPage(1);      // reset page on new search
            setResults([]);  // clear previous results
            setHasMore(true); // reset load more
        }, debounceTime);

        return () => clearTimeout(handler);
    }, [searchTerm]);

    // Trigger search when debounced term changes
    useEffect(() => {
        if (debouncedSearchTerm) {
            fetchResults(debouncedSearchTerm, 1);
        }
    }, [debouncedSearchTerm]);

    async function fetchResults(term, currentPage) {
        if (!hasMore && currentPage !== 1) return; // stop fetching if no more

        setLoading(true);
        try {
            const url = `${config.app.API_URL}/api/${config.app.REACT_APP_API_VERSION}/search?query=${encodeURIComponent(term)}&limit=50&page=${currentPage}`;
            const response = await fetch(url);
            const data = await response.json();

            if (data.results) {
                setResults(prev => currentPage === 1 ? data.results : [...prev, ...data.results]);

                // determine if there are more results
                if (data.results.length < 50) {
                    setHasMore(false);
                } else {
                    setHasMore(true);
                }
            }
        } catch (err) {
            console.error('Search fetch error:', err);
        } finally {
            setLoading(false);
        }
    }

    function handleInputChange(event) {
        setSearchTerm(event.target.value);
    }

    function loadMore() {
        if (!hasMore || loading) return;
        const nextPage = page + 1;
        setPage(nextPage);
        fetchResults(debouncedSearchTerm, nextPage);
    }

    return(
        <div className="search-wrapper" style={{ position: 'relative' }}>
            <div className="search-input-wrapper">
                <input
                    id="search-input"
                    placeholder="SEARCH"
                    type="text"
                    value={searchTerm}
                    onChange={handleInputChange}
                    autoComplete="off"
                />

                 {searchTerm && (
                    <button
                        type="button"
                        className="clear-button"
                        onClick={() => { setSearchTerm(''); setDebouncedSearchTerm(''); }}
                        aria-label="Clear search"
                    >
                        ×
                    </button>
                )}
            </div>
            

            {debouncedSearchTerm && (
                <div 
                    className="search-popup"
                    ref={popupRef}
                >
                    {results.length > 0 && (
                        <>
                            {results.map((product, idx) => (
                                <Link 
                                    key={idx} 
                                    to={`/products/${product.product_handle}`} 
                                    className="search-result-item-link"
                                >
                                    <div className="search-result-item">
                                        <div className="result-left">
                                            <div className="result-title">
                                                <strong>{product.title}</strong>
                                            </div>
                                            <div className="result-handle">{product.product_handle}</div>
                                            <div className="result-status">
                                                Status: <strong>{product.status.toUpperCase()}</strong>
                                            </div>
                                        </div>

                                        <div className="result-badges">
                                            {product.shop_info?.map((shop, sIdx) => (
                                                <div
                                                    key={sIdx}
                                                    className="badge"
                                                    style={{
                                                        color: shop.badge_text_color,
                                                        backgroundColor: shop.badge_background_color,
                                                    }}
                                                >
                                                    {shop.nickname}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </Link>
                            ))}
                        </>
                    )}

                    {results.length <= 0 && !loading && (
                        <div className="search-result-item no-results-item">
                            <div className="no-results-text">
                                No results found for "<strong>{debouncedSearchTerm}</strong>"
                            </div>
                        </div>
                    )}

                    {hasMore && !loading && (
                        <button className="load-more-button" onClick={loadMore}>
                            Load more
                        </button>
                    )}

                    { loading && 
                        <div className="search-result-item no-results-item">
                            <div className="no-results-text">
                                Loading results for "<strong>{debouncedSearchTerm}</strong>"
                            </div>
                        </div>
                    }
                </div>
            )}

            
        </div>
    );
}

export default Search;